<template>
    <div class="c-dashboard" :style="rootStyle">
        <!-- Loading -->
        <div v-if="isLoading" class="c-dashboard__loader" :style="loaderStyle">
            <clip-loader :loading="isLoading" color="#ec4657" size="45px" />
        </div>

        <!-- Main: agency action plan -->
        <div v-else-if="dashboard_data" class="c-dashboard__main">
            <div class="c-dashboard__inner" :style="innerStyle">
                <!-- Mobile copy follows the designer's MobileActionPlan_A:
                     date/eyebrow at top, personal greeting H1, walk-through
                     subtitle. Desktop keeps the agency-name framing. -->
                <div class="c-eyebrow" :style="eyebrowStyle">
                    <template v-if="isMobile">{{ currentMonthLong }} · ACTION PLAN</template>
                    <template v-else>Hi {{ agencyFirstName }} 👋</template>
                </div>
                <h1 :style="h1Style">
                    <template v-if="isMobile">
                        Hi {{ agencyFirstName }},<br>here's what's next.
                    </template>
                    <template v-else>
                        {{ dashboard_data.agency_name }}'s action plan for
                        <span :style="accentStyle">{{ currentMonth }}</span>.
                    </template>
                </h1>
                <p :style="leadStyle">
                    <template v-if="isMobile">
                        We'll walk through each brand together. You can pause anytime — everything autosaves.
                    </template>
                    <template v-else>
                        Here's what needs your attention this period. Estimated time: 4–6 minutes.
                    </template>
                </p>

                <!-- Overdue collections (grouped) — red-tinted card -->
                <CCard
                    v-if="groupedTasks.overdue.length"
                    :extra-style="overdueCardStyle"
                >
                    <div :style="taskHeadStyle">
                        <span :style="overdueBadgeStyle">!</span>
                        <h3 :style="taskTitleStyle">
                            <template v-if="isMobile">First, {{ groupedTasks.overdue.length }} overdue</template>
                            <template v-else>First, overdue collections</template>
                        </h3>
                    </div>
                    <p :style="groupLeadStyle">
                        <template v-if="isMobile">Collections haven't been logged for these deals.</template>
                        <template v-else>You haven't recorded collections for these deals from previous periods.</template>
                    </p>
                    <div :style="taskListStyle">
                        <div
                            v-for="(subtask, i) in groupedTasks.overdue"
                            :key="'overdue-' + i"
                            :style="brandRowStyle"
                        >
                            <span :style="brandRowAvatarStyle">{{ initialsOf(parseSubtaskName(subtask).brand) }}</span>
                            <div :style="brandRowBodyStyle">
                                <div :style="brandRowTitleStyle">{{ parseSubtaskName(subtask).brand }}</div>
                                <div :style="brandRowMetaStyle">
                                    <template v-if="isMobile">
                                        {{ subtask.type || 'Deal' }} · overdue
                                    </template>
                                    <template v-else>
                                        <span>{{ subtask.type || 'Deal' }}</span>
                                        <span v-if="parseSubtaskName(subtask).started"> · started {{ parseSubtaskName(subtask).started }}</span>
                                    </template>
                                </div>
                            </div>
                            <!-- Mobile: per-row Record pill. Routes through
                                 the same handleOnGo as the bottom CTA — the
                                 wizard's flow handles overdue items first
                                 internally, so the user lands on the right
                                 brand regardless of which row they tap. -->
                            <button
                                v-if="isMobile"
                                type="button"
                                :style="recordBtnStyle"
                                @click="handleOnGo"
                            >Record</button>
                        </div>
                    </div>
                </CCard>

                <!-- Active brands this period (grouped) -->
                <CCard
                    v-if="groupedTasks.active.length"
                    :extra-style="activeCardStyle"
                >
                    <div :style="taskHeadStyle">
                        <span :style="activeBadgeStyle">{{ groupedTasks.active.length }}</span>
                        <h3 :style="taskTitleStyle">Then, your active brands</h3>
                    </div>
                    <p :style="groupLeadStyle">
                        <template v-if="isMobile">For each, we'll log service, invoicing, and what was collected.</template>
                        <template v-else>For each, I'll ask whether you provided service and what was invoiced or collected.</template>
                    </p>
                    <div :style="brandGridStyle">
                        <div
                            v-for="(subtask, i) in groupedTasks.active"
                            :key="'active-' + i"
                            :style="brandChipStyle"
                        >
                            <span :style="brandChipAvatarStyle">{{ initialsOf(parseSubtaskName(subtask).brand) }}</span>
                            <div :style="brandChipBodyStyle">
                                <div :style="brandChipTitleStyle">{{ parseSubtaskName(subtask).brand }}</div>
                                <div :style="brandChipMetaStyle">{{ subtask.type || 'Retainer' }}</div>
                            </div>
                        </div>
                    </div>
                </CCard>

                <!-- Fallback: unknown task shapes get their original layout -->
                <CCard
                    v-for="(task, taskIdx) in groupedTasks.other"
                    :key="'other-' + taskIdx"
                    :extra-style="activeCardStyle"
                >
                    <div :style="taskHeadStyle">
                        <span :style="otherBadgeStyle">✦</span>
                        <h3 :style="taskTitleStyle" v-html="task.title"></h3>
                    </div>
                    <div :style="taskListStyle">
                        <div
                            v-for="(subtask, subIdx) in task.data"
                            :key="(subtask.name || '') + '-' + (subtask.id || subIdx)"
                            :style="taskRowStyle"
                        >
                            <span :style="taskRowDotStyle" />
                            <div :style="taskRowBodyStyle">
                                <template v-if="subtask.type === 'kickoff'">
                                    <span :style="taskRowLabelStyle">Deal</span>
                                    <span :style="taskRowValueStyle">{{ subtask.name }}</span>
                                    <span :style="taskRowSepStyle">·</span>
                                    <span :style="taskRowLabelStyle">Service</span>
                                    <span :style="taskRowValueStyle">{{ subtask.services }}</span>
                                </template>
                                <template v-else>
                                    <span :style="taskRowLabelStyle">{{ subtask.type }}</span>
                                    <span :style="taskRowValueStyle">{{ subtask.name }}</span>
                                    <span
                                        v-if="subtask.type === 'Invoice'"
                                        :style="taskRowAmountStyle"
                                    >${{ subtask.amount }}</span>
                                </template>
                            </div>
                        </div>
                    </div>
                </CCard>

                <div :style="ctaWrapStyle">
                    <CBtn
                        primary
                        big
                        :extra-style="ctaBtnStyle"
                        @click="handleOnGo"
                    >
                        Let's get started →
                    </CBtn>
                </div>
            </div>
        </div>

        <!-- No agency state -->
        <div v-else class="c-dashboard__empty">
            <div :style="innerStyle">
                <CCard :extra-style="{ padding: '40px 36px', textAlign: 'center' }">
                    <div :style="emptyIconStyle">!</div>
                    <h2 :style="emptyTitleStyle">No agency is associated with your account.</h2>
                    <p :style="emptyBodyStyle">
                        Please contact the administrator to be linked with an agency before continuing.
                    </p>
                </CCard>
            </div>
        </div>
    </div>
</template>

<script>
import ClipLoader from "vue-spinner/src/ClipLoader";
import axios from "axios";
import moment from "moment";
import { SECTIONS } from "../consts";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import {
    C_BG,
    C_INK,
    C_INK2,
    C_MUTED,
    C_RED,
    C_HAIR,
    C_CREAM,
    TAG_RED_DEEP,
    FONT_DISPLAY,
    FONT_BODY,
} from "../c-design/tokens";

export default {
    name: "Wizard_Dashboard",
    mixins: [viewportMixin],
    props: ["onGo", "toEditSales", "onHandleUploadLater"],
    components: {
        ClipLoader,
        CCard,
        CBtn,
    },
    data() {
        return {
            isLoading: false,
            dashboard_data: null,
            isHasKickOff: false,
            isHasUploadLaterSOWs: false,
        };
    },
    mounted() {
        this.getTasks();

        scrollToTop();
    },
    computed: {
        currentMonth: () => {
            return moment().format("MMMM'YY");
        },
        // Long form for mobile eyebrow ("April 2026" instead of "April'26").
        currentMonthLong: () => {
            return moment().format("MMMM YYYY");
        },
        agencyFirstName() {
            if (!this.dashboard_data || !this.dashboard_data.agency_name) return '';
            // Take the first word — friendly greeting without surfacing legal-name tail.
            return this.dashboard_data.agency_name.split(' ')[0];
        },
        // The backend returns one task PER (brand × condition), so a 7-brand
        // agency shows 9+ near-identical "In April you had active deals with…"
        // rows. The prototype collapses those into two thematic cards:
        // "First, overdue collections" + "Then, your active brands". This
        // computed does that grouping by title pattern; anything unknown
        // falls through to `other` so the screen never goes blank.
        groupedTasks() {
            const empty = { overdue: [], active: [], other: [] };
            if (!this.dashboard_data || !Array.isArray(this.dashboard_data.page)) {
                return empty;
            }
            const overdue = [];
            const active = [];
            const other = [];
            for (const task of this.dashboard_data.page) {
                const title = (task.title || '').toLowerCase();
                const items = Array.isArray(task.data) ? task.data : [];
                if (title.includes('overdue')) {
                    overdue.push(...items);
                } else if (title.includes('active deal')) {
                    active.push(...items);
                } else {
                    other.push(task);
                }
            }
            return { overdue, active, other };
        },
        // ===== c-design inline-style maps =====
        rootStyle() {
            return {
                background: C_BG,
                fontFamily: FONT_BODY,
                color: C_INK,
                minHeight: '70vh',
                // Bigger bottom padding on mobile so content isn't hidden
                // under the sticky "Let's get started" CTA.
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return {
                maxWidth: '780px',
                margin: '0 auto',
            };
        },
        loaderStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: this.isMobile ? '8px 0 14px' : '8px 0 14px',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '36px' : '42px',
                fontWeight: 700,
                letterSpacing: this.isMobile ? '-1px' : '-1.2px',
                lineHeight: this.isMobile ? 1.05 : 1.05,
                color: C_INK,
            };
        },
        accentStyle() {
            return { color: C_RED };
        },
        leadStyle() {
            return {
                margin: 0,
                fontSize: this.isMobile ? '13.5px' : '16px',
                color: C_MUTED,
                lineHeight: 1.55,
                maxWidth: this.isMobile ? 'none' : '560px',
            };
        },
        taskHeadStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '4px',
            };
        },
        taskTitleStyle() {
            return {
                margin: 0,
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '16px' : '18px',
                fontWeight: 700,
                letterSpacing: '-0.4px',
                color: C_INK,
            };
        },
        taskListStyle() {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginTop: '14px',
            };
        },
        taskRowStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 14px',
                background: C_CREAM,
                borderRadius: '12px',
                fontSize: '14px',
            };
        },
        taskRowDotStyle() {
            return {
                width: '6px',
                height: '6px',
                borderRadius: '3px',
                background: C_RED,
                flexShrink: 0,
            };
        },
        taskRowBodyStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '6px',
                color: C_INK2,
            };
        },
        taskRowLabelStyle() {
            return {
                fontSize: '12px',
                color: C_MUTED,
                fontWeight: 500,
                textTransform: 'capitalize',
            };
        },
        taskRowValueStyle() {
            return {
                fontWeight: 600,
                color: C_INK,
            };
        },
        taskRowSepStyle() {
            return { color: C_HAIR };
        },
        taskRowAmountStyle() {
            return {
                marginLeft: 'auto',
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                color: TAG_RED_DEEP,
            };
        },
        ctaWrapStyle() {
            // Mobile: pin "Let's get started" to the bottom of the viewport.
            // Reusing the nav-mobile-sticky pattern from tokens.js but with
            // justifyContent: 'center' since it's a single CTA, not a
            // Previous/Next pair.
            if (this.isMobile) {
                return {
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '12px 16px calc(env(safe-area-inset-bottom, 12px) + 12px)',
                    background: 'rgba(245,241,234,0.94)',
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)',
                    borderTop: `1px solid ${C_HAIR}`,
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 50,
                };
            }
            return {
                marginTop: '28px',
                display: 'flex',
                justifyContent: 'center',
            };
        },
        ctaBtnStyle() {
            // Mobile sticky CTA fills the bar; desktop keeps the natural
            // pill width centered by the wrap.
            return this.isMobile
                ? { width: '100%', borderRadius: '18px' }
                : {};
        },
        activeCardStyle() {
            return {
                marginTop: this.isMobile ? '14px' : '18px',
                padding: this.isMobile ? '20px' : '28px',
            };
        },
        emptyIconStyle() {
            return {
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: C_RED,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                fontWeight: 700,
                marginBottom: '14px',
            };
        },
        emptyTitleStyle() {
            return {
                margin: '0 0 8px',
                fontFamily: FONT_DISPLAY,
                fontSize: '22px',
                fontWeight: 700,
                letterSpacing: '-0.4px',
                color: C_INK,
            };
        },
        emptyBodyStyle() {
            return {
                margin: 0,
                fontSize: '14px',
                color: C_MUTED,
                lineHeight: 1.55,
            };
        },
        // ===== Grouped-card style maps =====
        overdueCardStyle() {
            return {
                marginTop: this.isMobile ? '18px' : '28px',
                padding: this.isMobile ? '20px' : '28px',
                background: '#FFF5F5',
                boxShadow:
                    '0 1px 0 #F5DEDA, 0 24px 48px -28px rgba(200,40,60,0.18)',
            };
        },
        overdueBadgeStyle() {
            return {
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: C_RED,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: '15px',
                flexShrink: 0,
            };
        },
        activeBadgeStyle() {
            return {
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: C_INK,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: '13px',
                flexShrink: 0,
            };
        },
        otherBadgeStyle() {
            return {
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: '#fff',
                border: '1px solid ' + C_HAIR,
                color: C_INK,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: '14px',
                flexShrink: 0,
            };
        },
        groupLeadStyle() {
            return {
                margin: this.isMobile ? '6px 0 14px 36px' : '8px 0 18px 38px',
                fontSize: this.isMobile ? '12.5px' : '14px',
                color: C_INK2,
                lineHeight: 1.5,
            };
        },
        // ===== Overdue row (big card-row with avatar) =====
        brandRowStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: this.isMobile ? '12px' : '14px',
                padding: this.isMobile ? '12px 14px' : '14px 18px',
                background: '#fff',
                borderRadius: '14px',
                marginBottom: '8px',
            };
        },
        brandRowAvatarStyle() {
            return {
                width: this.isMobile ? '36px' : '38px',
                height: this.isMobile ? '36px' : '38px',
                borderRadius: this.isMobile ? '11px' : '12px',
                background: C_BG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '12px' : '14px',
                fontWeight: 700,
                color: C_INK,
                flexShrink: 0,
            };
        },
        brandRowBodyStyle() {
            return { flex: 1, minWidth: 0 };
        },
        brandRowTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '14px' : '16px',
                fontWeight: 600,
                color: C_INK,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            };
        },
        brandRowMetaStyle() {
            return {
                fontSize: this.isMobile ? '11px' : '13px',
                color: C_MUTED,
                marginTop: '1px',
            };
        },
        // ===== Active-brands grid =====
        // Mobile: 1-col stacked chips per designer's MobileActionPlan_A.
        // Desktop: 2-col compact chips.
        brandGridStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '8px' : '10px',
            };
        },
        brandChipStyle() {
            return {
                display: 'flex',
                alignItems: 'center',
                gap: this.isMobile ? '12px' : '12px',
                padding: this.isMobile ? '12px 14px' : '12px 14px',
                background: C_CREAM,
                borderRadius: '12px',
                // Defensive — see Dashboard brand-grid overflow fix (49ce2b0).
                minWidth: 0,
                overflow: 'hidden',
            };
        },
        brandChipAvatarStyle() {
            return {
                width: this.isMobile ? '40px' : '34px',
                height: this.isMobile ? '40px' : '34px',
                borderRadius: this.isMobile ? '11px' : '10px',
                background: '#fff',
                border: '1px solid ' + C_HAIR,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '12px' : '12px',
                fontWeight: 700,
                color: C_INK,
                flexShrink: 0,
            };
        },
        brandChipBodyStyle() {
            return { flex: 1, minWidth: 0 };
        },
        brandChipTitleStyle() {
            return {
                fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '15px' : '14px',
                fontWeight: 700,
                color: C_INK,
                letterSpacing: '-0.2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            };
        },
        brandChipMetaStyle() {
            return {
                fontSize: this.isMobile ? '12px' : '11px',
                color: C_MUTED,
                marginTop: '2px',
            };
        },
        // Mobile-only red pill button on each overdue row.
        recordBtnStyle() {
            return {
                padding: '8px 14px',
                background: C_RED,
                color: '#fff',
                border: 'none',
                borderRadius: '999px',
                fontFamily: FONT_BODY,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
            };
        },
    },
    methods: {
        // Strip the " - Started Date: ..." suffix that the backend
        // concatenates into `subtask.name`, returning brand + started
        // as separate fields so the chip can render them in their own
        // typographic slots.
        parseSubtaskName(subtask) {
            const raw = (subtask && subtask.name) || '';
            const m = raw.match(/^(.*?)\s*-\s*Started\s*Date:\s*(.+)$/i);
            if (m) return { brand: m[1].trim(), started: m[2].trim() };
            return { brand: raw, started: '' };
        },
        // Build 2-letter avatar initials from a brand name.
        initialsOf(name) {
            if (!name) return '?';
            const words = name.trim().split(/\s+/);
            return words.slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
        },
        handleOnGo() {
            if (this.isHasUploadLaterSOWs) {
                this.onHandleUploadLater();
            } else if (this.isHasKickOff) {
                this.onGo();
            } else {
                this.toEditSales();
            }
        },
        async getTasks() {
            try {
                this.isLoading = true;

                await axios
                    .post("/api/wizard/getList", { page: "dashboard" })
                    .then((res) => {
                        const { data } = res;

                        if (
                            typeof data === "object" &&
                            !Array.isArray(data) &&
                            data !== null
                        ) {
                            this.dashboard_data = data;
                            this.$store.commit(
                                "setAgencyName",
                                data.agency_name
                            );

                            if (!data.page.length) {
                                this.$store.commit(
                                    "setCurrentSection",
                                    SECTIONS.NO_ACTIONS
                                );
                            }

                            return;
                        }

                        this.dashboard_data = null;
                    })
                    .finally(() => {
                        this.checkIfHasKickOffDeals();
                        this.checkIfHasUploadLaterSOWs();
                        this.isLoading = false;
                    });
            } catch (err) {
                console.error(err);
            }
        },
        async checkIfHasKickOffDeals() {
            await axios
                .post("/api/wizard/getList", {
                    page: "edit_upsell",
                })
                .then((res) => {
                    if (Object.values(res.data.page.deals).length > 0) {
                        this.isHasKickOff = true;
                        this.$store.commit("setIsHasKickOff", true);
                    } else {
                        this.$store.commit("setIsHasKickOff", false);
                    }
                })
                .catch((err) => console.log("err getData upsell", err));
        },

        async checkIfHasUploadLaterSOWs() {
            await axios
                .post("/api/wizard/getList", {
                    page: "get_upload_later",
                })
                .then((res) => {
                    if (Object.values(res.data.page).length > 0) {
                        this.isHasUploadLaterSOWs = true;
                        this.$store.commit("setIsHasUploadLaterSOWs", true);
                    } else {
                        this.$store.commit("setIsHasUploadLaterSOWs", false);
                    }
                })
                .catch((err) => console.log("err getData upload later", err));
        },
    },
};
</script>
